import i18n from 'client/i18n'
import { createContainerStructure } from 'client/utils/createStructureUtils'
import { ColumnTypeEnum } from 'common/components/entities/Column/enums/ColumnTypeEnum'
import { FieldSlugEnum } from 'common/enums/FieldSlugEnum'
import { BaseEntityWithChildIdsInterface } from 'common/types/entities/EntityInterface'
import { OldEntityWithChildIds } from 'common/types/entities/OldEntityInterface'
import { createField } from 'src/shared/entities/field/create-field'
import { createOfferPrice } from 'src/shared/entities/offer-price/create-offer-price'
import { createPaymentMethod } from 'src/shared/entities/payment-method/create-payment-method'
import { createRowColumnLayout } from 'src/shared/entities/row/create-row-column-layout'
import { createText } from 'src/shared/entities/text/create-text'
import {
  createTwoStepPaymentForm,
  createTwoStepPaymentFormStepOptIn,
  createTwoStepPaymentFormStepPayment,
} from 'src/shared/entities/two-step-payment-form/create-two-step-payment-form'
import { insertChild } from 'src/shared/entities/utils/generate-base-entity'

const TWO_STEP_FIELD_MARGIN = {
  marginTop: 5,
  marginRight: 0,
  marginBottom: 5,
  marginLeft: 0,
}

const TWO_STEP_ROW_PADDING = {
  paddingTop: 5,
  paddingRight: 0,
  paddingBottom: 5,
  paddingLeft: 0,
}

export const createTwoStepPaymentFormFromLibrary = (
  container: BaseEntityWithChildIdsInterface | OldEntityWithChildIds,
  locale: string,
  position = 0,
) => {
  const fixedT = i18n.getFixedT(locale)

  const { structure, descendantEntity } = createContainerStructure(
    container,
    locale,
    position,
  )

  const twoStepPaymentForm = createTwoStepPaymentForm(
    descendantEntity.id,
    descendantEntity.masterBlockId,
  )

  const twoStepPaymentFormStepOptIn = createTwoStepPaymentFormStepOptIn({
    parentId: twoStepPaymentForm.id,
    headline: fixedT(
      'entities.two_step_payment_form.step_opt_in.default_headline',
    ),
    subHeadline: fixedT(
      'entities.two_step_payment_form.step_opt_in.default_sub_headline',
    ),
    buttonText: fixedT(
      'entities.two_step_payment_form.step_opt_in.default_button_text',
    ),
    buttonSubText: fixedT(
      'entities.two_step_payment_form.step_opt_in.default_button_sub_text',
    ),
    footerText: fixedT(
      'entities.two_step_payment_form.step_opt_in.default_footer_text',
    ),
  })

  const twoStepPaymentFormStepPayment = createTwoStepPaymentFormStepPayment({
    parentId: twoStepPaymentForm.id,
    headline: fixedT(
      'entities.two_step_payment_form.step_payment.default_headline',
    ),
    subHeadline: fixedT(
      'entities.two_step_payment_form.step_payment.default_sub_headline',
    ),
    buttonText: fixedT(
      'entities.two_step_payment_form.step_payment.default_button_text',
    ),
    buttonSubText: fixedT(
      'entities.two_step_payment_form.step_payment.default_button_sub_text',
    ),
    footerText: fixedT(
      'entities.two_step_payment_form.step_payment.default_footer_text',
    ),
  })

  const {
    row: optInRow1,
    column1: optInColumn11,
    column2: optInColumn12,
  } = createRowColumnLayout({
    parentEntity: twoStepPaymentFormStepOptIn,
    position,
    columnType: ColumnTypeEnum.COLUMN_6,
  })
  optInRow1.padding = TWO_STEP_ROW_PADDING

  const firstNameField = createField(
    optInColumn11.id,
    optInColumn11.masterBlockId,
    {
      slug: FieldSlugEnum.FirstName,
      placeholder: fixedT('entities.field.first_name.default_placeholder'),
      margin: TWO_STEP_FIELD_MARGIN,
    },
  )

  const lastNameField = createField(
    optInColumn12.id,
    optInColumn12.masterBlockId,
    {
      slug: FieldSlugEnum.LastName,
      placeholder: fixedT('entities.field.last_name.default_placeholder'),
      margin: TWO_STEP_FIELD_MARGIN,
    },
  )

  const { row: optInRow2, column1: optInColumn2 } = createRowColumnLayout({
    parentEntity: twoStepPaymentFormStepOptIn,
    position,
  })
  optInRow2.padding = TWO_STEP_ROW_PADDING

  const emailField = createField(optInColumn2.id, optInColumn2.masterBlockId, {
    slug: FieldSlugEnum.Email,
    placeholder: fixedT('entities.field.email.default_placeholder'),
    margin: TWO_STEP_FIELD_MARGIN,
  })

  const shippingText = createText(
    optInColumn2.id,
    optInColumn2.masterBlockId,
    fixedT('entities.two_step_payment_form.step_opt_in.default_shipping_text'),
  )
  shippingText.textAlign = 'left'
  shippingText.margin = {
    marginTop: 10,
    marginRight: 0,
    marginBottom: 0,
    marginLeft: 2,
  }
  shippingText.fontSize = 12

  const streetAddressField = createField(
    optInColumn2.id,
    optInColumn2.masterBlockId,
    {
      slug: FieldSlugEnum.StreetAddress,
      placeholder: fixedT('entities.field.street_address.default_placeholder'),
      margin: TWO_STEP_FIELD_MARGIN,
    },
  )

  const cityField = createField(optInColumn2.id, optInColumn2.masterBlockId, {
    slug: FieldSlugEnum.City,
    placeholder: fixedT('entities.field.city.default_placeholder'),
    margin: TWO_STEP_FIELD_MARGIN,
  })

  const countryField = createField(
    optInColumn2.id,
    optInColumn2.masterBlockId,
    {
      slug: FieldSlugEnum.Country,
      placeholder: fixedT('entities.field.country.placeholder'),
      margin: TWO_STEP_FIELD_MARGIN,
    },
  )

  const postCodeField = createField(
    optInColumn2.id,
    optInColumn2.masterBlockId,
    {
      slug: FieldSlugEnum.Postcode,
      placeholder: fixedT('entities.field.post_code.default_placeholder'),
      margin: TWO_STEP_FIELD_MARGIN,
    },
  )

  const { row: paymentRow, column1: paymentColumn } = createRowColumnLayout({
    parentEntity: twoStepPaymentFormStepPayment,
    position,
  })
  paymentRow.padding = TWO_STEP_ROW_PADDING

  const offerPrice = createOfferPrice(
    paymentColumn.id,
    paymentColumn.masterBlockId,
  )

  const paymentMethod = createPaymentMethod(
    paymentColumn.id,
    paymentColumn.masterBlockId,
  )

  return {
    ...structure,
    [descendantEntity.id]: {
      ...descendantEntity,
      childIds: insertChild(
        descendantEntity.childIds,
        twoStepPaymentForm.id,
        position,
      ),
    },
    [twoStepPaymentForm.id]: {
      ...twoStepPaymentForm,
      childIds: [
        twoStepPaymentFormStepOptIn.id,
        twoStepPaymentFormStepPayment.id,
      ],
    },
    [twoStepPaymentFormStepOptIn.id]: {
      ...twoStepPaymentFormStepOptIn,
      childIds: [optInRow1.id, optInRow2.id],
    },
    [twoStepPaymentFormStepPayment.id]: {
      ...twoStepPaymentFormStepPayment,
      childIds: [paymentRow.id],
    },
    [optInRow1.id]: optInRow1,
    [optInColumn11.id]: { ...optInColumn11, childIds: [firstNameField.id] },
    [optInColumn12.id]: { ...optInColumn12, childIds: [lastNameField.id] },
    [firstNameField.id]: firstNameField,
    [lastNameField.id]: lastNameField,
    [optInRow2.id]: optInRow2,
    [optInColumn2.id]: {
      ...optInColumn2,
      childIds: [
        emailField.id,
        shippingText.id,
        streetAddressField.id,
        cityField.id,
        countryField.id,
        postCodeField.id,
      ],
    },
    [emailField.id]: emailField,
    [shippingText.id]: shippingText,
    [streetAddressField.id]: streetAddressField,
    [cityField.id]: cityField,
    [countryField.id]: countryField,
    [postCodeField.id]: postCodeField,
    [paymentRow.id]: paymentRow,
    [paymentColumn.id]: {
      ...paymentColumn,
      childIds: [offerPrice.id, paymentMethod.id],
    },
    [offerPrice.id]: offerPrice,
    [paymentMethod.id]: paymentMethod,
  }
}
